import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** Set once per tab when a failed chunk triggered a reload, so a broken build cannot loop. */
export const RELOAD_KEY = 'mantisbt.chunkReload';

// A navigation away cancels in-flight chunk requests, and WebKit rejects the import before
// `pagehide` fires; `beforeunload` comes first. The reload also waits a moment: a document that is
// being left is gone by then, so it can never hijack the navigation the user started.
let leaving = false;
if (typeof window !== 'undefined')
  for (const type of ['beforeunload', 'pagehide'])
    window.addEventListener(type, () => (leaving = true));
const RELOAD_DELAY_MS = 500;

/** "Failed to fetch dynamically imported module" (Chromium), "error loading dynamically imported
 * module" (Firefox), "Importing a module script failed" (WebKit). */
export function isChunkLoadError(e: unknown): boolean {
  return /dynamically imported module|Importing a module script failed/i.test(String(e));
}

const never = <T>() => new Promise<T>(() => undefined);

/**
 * React.lazy for a page chunk, surviving the two ways a chunk fails to load in normal use:
 * - A deploy replaced the hashed assets under an open tab: reload once, which fetches the new
 *   index.html (React.lazy caches the rejection, so "Try again" alone can never recover).
 * - The document is being left while the chunk loads (the browser cancels it; WebKit then rejects
 *   the import and would log a crash for a page nobody sees): stay pending.
 * Any other error, or a second failure after the reload, reaches the error boundary as before.
 */
export function lazyPage<T extends ComponentType>(
  load: () => Promise<{ default: T }>,
  reload: () => void = () => window.location.reload(),
): LazyExoticComponent<T> {
  return lazy(() =>
    load().then(
      (m) => {
        sessionStorage.removeItem(RELOAD_KEY);
        return m;
      },
      (e: unknown) => {
        if (leaving) return never<{ default: T }>();
        if (isChunkLoadError(e) && !sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          setTimeout(() => {
            if (leaving) sessionStorage.removeItem(RELOAD_KEY);
            else reload();
          }, RELOAD_DELAY_MS);
          return never<{ default: T }>();
        }
        throw e;
      },
    ),
  );
}
